import { useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router';

import { IconButton, TableBody, TableCell, TableHead, TableRow, Tooltip, Button, TableContainer, Paper } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DatasetIcon from '@mui/icons-material/Dataset';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import AddIcon from '@mui/icons-material/Add';

import { CustomTable } from 'esocial.web.react.lib/cjs/components/util/Table';
import { TituloPagina, TituloSecao } from 'esocial.web.react.lib/cjs/components/util/Text';
import { ContainerSecao, ContainerLateral } from 'esocial.web.react.lib/cjs/components/simples/Containers';

import { CelulaFonteMenor, CelulaFonteMenorSemPadding } from 'esocial.web.react.lib/cjs/components/simples/FormatacaoTabelas';
import { CampoComMascara, MascaraEnum } from 'esocial.web.react.lib/cjs/components/simples/campos/CampoComMascara';
import { SeletorOpcoes } from 'esocial.web.react.lib/cjs/components/simples/campos/SeletorOpcoes';
import { idInformacoesUsuarioArmazenamentoBrowser, obterAcessoArmazenamentoBrowser } from 'esocial.web.react.lib/cjs/utils/servicos';

import { useAppDispatch } from 'esocial.web.react.lib/cjs/hooks/Reduz';
import { useRequisicaoControlada } from 'esocial.web.react.lib/cjs/hooks/RequisicaoControlada';

import { useRelatoriosGerenciaisContext } from '../../contexts/RelatoriosGerenciaisContext';

import { getRelatorios, getConstantes, downloadFile } from './auxiliares/RelatoriosGerenciaisRequests';
import TextoColapsavel from './auxiliares/TextoColapsavel';
import InformacoesLimitesRelatorio from './auxiliares/InfoLimitesRelatorio';

function isNullOrEmpty(variavel) {
    return variavel == null || variavel == '';
}

const IdentificadorGeral = () => {
    const { tipoInscricaoEmpregador, setTipoInscricaoEmpregador,
        numeroInscricaoEmpregador, setNumeroInscricaoEmpregador,
        tipoRelatorio, setTipoRelatorio,
        opcoesTipoRelatorio, opcoesTipoInscricao } = useRelatoriosGerenciaisContext();

    useEffect(() => {
        // seleciona automaticamente o tipo de relatório de trabalhadores
        // será alterado a medida que existirem novos relatórios
        setTipoRelatorio(opcoesTipoRelatorio[0].value);
    }, [opcoesTipoRelatorio])

    return (
        <ContainerSecao sx={{ gap: '1rem' }}>
            <SeletorOpcoes
                label={'Tipo do Relatório'}
                obrigatorio={true}
                variavel={tipoRelatorio}
                setVariavel={setTipoRelatorio}
                opcoes={opcoesTipoRelatorio}
                bloqueado={true}
                maxWidth={500}
            />
            <ContainerLateral>
                <SeletorOpcoes
                    bloqueado={true}
                    label={'Tipo empregador'}
                    obrigatorio={true}
                    variavel={tipoInscricaoEmpregador}
                    setVariavel={setTipoInscricaoEmpregador}
                    opcoes={opcoesTipoInscricao}
                />
                <CampoComMascara
                    mascaraTipo={tipoInscricaoEmpregador == 1 ? MascaraEnum.CNPJ : MascaraEnum.CPF}
                    bloqueado={true}
                    obrigatorio={true}
                    label={'Número de inscrição'}
                    variavel={numeroInscricaoEmpregador}
                    setVariavel={setNumeroInscricaoEmpregador}
                    width={250}
                />
            </ContainerLateral>
        </ContainerSecao>
    )
}

const RelatoriosSolicitados = () => {
    const { relatoriosSolicitados, opcoesTipoRelatorio,
        formatFiltro, formatDate, formatDisponivelAte,
        descricaoSituacao } = useRelatoriosGerenciaisContext();

    const { get } = useRequisicaoControlada();

    const dispatch = useAppDispatch();

    const LIMITE_PDF = 1000;
    const LIMITE_XLSX = 20000;

    const legendaBotaoDownload = (tipoArquivo, situacao, passouLimite) => {
        if (passouLimite || situacao == 7)
            return tipoArquivo + ': Limite de tamanho excedido';
        if (situacao == 1 || situacao == 8 || situacao == 9)
            return tipoArquivo + ' em procecssamento';
        if (situacao == 3)
            return tipoArquivo + ' não está mais disponível';
        if (situacao == 4)
            return tipoArquivo + ' vazio';
        if (situacao == 2)
            return 'Baixar ' + tipoArquivo;
    }

    return (
        <TableContainer component={Paper} sx={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '400px' }}>
            <CustomTable>
                <TableHead id="tabela de relatórios socilitados" aria-label="tabela de relatórios solicitados">
                    <TableRow>
                        <TableCell align="center">Tipo de relatório</TableCell>
                        <TableCell align="center" size="small">Registros recuperados</TableCell>
                        <TableCell align="center">Filtros Aplicados</TableCell>
                        <TableCell align="center">Solicitado Em</TableCell>
                        <TableCell align="center">Disponível Até</TableCell>
                        <TableCell align="center">Estado</TableCell>
                        <TableCell align="center" sx={{ width: '135px' }}>Opções de Download</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {relatoriosSolicitados.length > 0 ?
                        (relatoriosSolicitados.map((linha) => {
                                return (
                                    <TableRow key={linha.id}>
                                        <CelulaFonteMenor align="center">{opcoesTipoRelatorio.find(tipo => tipo.value == linha.tipo).label}</CelulaFonteMenor>
                                        <CelulaFonteMenor align="center">{linha.registrosRecuperados !== null ? linha.registrosRecuperados : '-'}</CelulaFonteMenor>
                                        <CelulaFonteMenor align="left">
                                            <TextoColapsavel maxWidth={310}>
                                                {formatFiltro(linha.filtro)}
                                            </TextoColapsavel>
                                        </CelulaFonteMenor>
                                        <CelulaFonteMenor align="center">{formatDate(linha.solicitacao)}</CelulaFonteMenor>
                                        <CelulaFonteMenor align="center">{formatDisponivelAte(linha.processamento, linha.situacao)}</CelulaFonteMenor>
                                        <CelulaFonteMenor align="center">{descricaoSituacao[linha.situacao]}</CelulaFonteMenor>
                                        <CelulaFonteMenorSemPadding align="center">
                                            <Tooltip title={legendaBotaoDownload("PDF", linha.situacao, linha.registrosRecuperados > LIMITE_PDF)}>
                                                <span>
                                                    <IconButton
                                                        disabled={(!linha.arquivo?.nome && linha.situacao != 4) || linha.situacao == 3 || linha.registrosRecuperados > LIMITE_PDF}
                                                        aria-label="baixar como pdf"
                                                        onClick={() => downloadFile(linha, dispatch, get, 'application/pdf')}
                                                    >
                                                        <PictureAsPdfIcon />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title={legendaBotaoDownload("CSV", linha.situacao, false)}>
                                                <span>
                                                    <IconButton
                                                        disabled={linha.arquivo.nome == null || linha.situacao == 3}
                                                        aria-label="baixar como csv"
                                                        onClick={() => downloadFile(linha, dispatch, get, 'text/csv')}
                                                    >
                                                        <TextSnippetIcon />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title={legendaBotaoDownload("XLSX", linha.situacao, linha.registrosRecuperados > LIMITE_XLSX)}>
                                                <span>
                                                    <IconButton
                                                        disabled={linha.arquivo.nome == null || linha.situacao == 3 || linha.registrosRecuperados > LIMITE_XLSX}
                                                        aria-label="baixar como xlsx"
                                                        onClick={() => downloadFile(linha, dispatch, get, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')}
                                                    >
                                                        <DatasetIcon />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </CelulaFonteMenorSemPadding>
                                    </TableRow>
                                )
                            })) : (
                            <TableRow>
                                <TableCell align="center" colSpan={9}>
                                    Nenhum registro foi encontrado.
                                </TableCell>
                            </TableRow>
                        )
                    }
                </TableBody>
            </CustomTable>
        </TableContainer>
    )
}

const RelatoriosGerenciaisListagem = () => {

    const { setTipoInscricaoEmpregador, setNumeroInscricaoEmpregador, setTipoInscricaoSolicitante, tipoInscricaoSolicitante,
        setNumeroInscricaoSolicitante, numeroInscricaoSolicitante, setRelatoriosSolicitados, relatoriosSolicitados,
        opcoesCBO, setOpcoesTipoRelatorio, setDescricaoSituacao, setOpcoesTipoEstabelecimento, setOpcoesCBO, setOpcoesCategorias,
        tipoInscricaoEmpregador, numeroInscricaoEmpregador
    } = useRelatoriosGerenciaisContext();

    const dispatch = useAppDispatch();
    const { get } = useRequisicaoControlada();
    const navigate = useNavigate();

    //Executado ao entrar na página
    useEffect(() => {
        async function getInformacoesUsuario() {
            const informacoesUsuario = await obterAcessoArmazenamentoBrowser().get(idInformacoesUsuarioArmazenamentoBrowser);
            if (informacoesUsuario !== null) {
                let numInscSolicitante;
                let tpInscSolicitante;
                if (informacoesUsuario.usuario.usuarioRepresentante == null) {
                    numInscSolicitante = informacoesUsuario.usuario.empregador.numeroInscricao;
                    tpInscSolicitante = informacoesUsuario.usuario.empregador.tipoInscricao;
                    setTipoInscricaoSolicitante(tpInscSolicitante);
                    setNumeroInscricaoSolicitante(numInscSolicitante);
                    setTipoInscricaoEmpregador(tpInscSolicitante);
                    setNumeroInscricaoEmpregador(numInscSolicitante);
                } else {
                    // Atendente representando um outro empregador
                    numInscSolicitante = informacoesUsuario.usuario.usuarioRepresentante.numeroInscricao;
                    tpInscSolicitante = informacoesUsuario.usuario.usuarioRepresentante.tipoInscricao;
                    if (informacoesUsuario.usuario.tipo == "atendente") {
                        tpInscSolicitante = 2;
                    } else if (informacoesUsuario.usuario.tipo == "representanteLegal") {
                        tpInscSolicitante = 2;
                    } else if (informacoesUsuario.usuario.tipo == "procurador") {
                        if (numInscSolicitante.length >= 18) {
                            tpInscSolicitante = 1;
                        } else {
                            tpInscSolicitante = 2;
                        }
                    }
                    setTipoInscricaoSolicitante(tpInscSolicitante);
                    setNumeroInscricaoSolicitante(numInscSolicitante);
                    let numInscEmpregador = informacoesUsuario.usuario.empregador.numeroInscricao;
                    let tpInscEmpregador = informacoesUsuario.usuario.empregador.tipoInscricao;
                    setTipoInscricaoEmpregador(tpInscEmpregador);
                    setNumeroInscricaoEmpregador(numInscEmpregador);
                }
            }
        }
        getInformacoesUsuario();
    }, [])

    useEffect(() => {
        if (opcoesCBO.length == 1) { //significa que não foi carregado
            getConstantes(setOpcoesTipoRelatorio, setDescricaoSituacao, setOpcoesTipoEstabelecimento, setOpcoesCBO, setOpcoesCategorias, dispatch);
        }
    }, []);

    useEffect(() => {
        let precisaAtualizar = false;
        if (relatoriosSolicitados.length !== 0) {
            if (relatoriosSolicitados[0].solicitante.numero !== numeroInscricaoSolicitante.replace(/[^a-zA-Z0-9]/g, '')
                || relatoriosSolicitados[0].empregador.numero !== numeroInscricaoEmpregador.replace(/[^a-zA-Z0-9]/g, '')) {
                precisaAtualizar = true;
            }
        }
        if ((relatoriosSolicitados.length == 0 || precisaAtualizar) && opcoesCBO.length !== 1 && !isNullOrEmpty(numeroInscricaoSolicitante) && !isNullOrEmpty(numeroInscricaoEmpregador)) {
            getRelatorios(tipoInscricaoEmpregador, numeroInscricaoEmpregador, tipoInscricaoSolicitante, numeroInscricaoSolicitante, setRelatoriosSolicitados, dispatch, get);
        }
    }, [numeroInscricaoSolicitante, opcoesCBO, numeroInscricaoEmpregador])

    return (
        <Fragment>
            <TituloPagina sx={{ color: '#5E7D61' }}>
                Relatórios Gerenciais
            </TituloPagina>

            <IdentificadorGeral />

            <ContainerLateral sx={{marginBottom: '3rem'} }>
                <Button
                    aria-label={'listar solicitações de relatório'}
                    startIcon={<FormatListBulletedIcon />}
                    variant="contained"
                    onClick={() => getRelatorios(tipoInscricaoEmpregador, numeroInscricaoEmpregador, tipoInscricaoSolicitante, numeroInscricaoSolicitante, setRelatoriosSolicitados, dispatch, get)}
                    color="primary"
                    size="medium">
                    Listar Solicitações
                </Button>
                <Button
                    aria-label={'criar nova solicitação de relatório'}
                    startIcon={<AddIcon/>}
                    variant="contained"
                    onClick={() => navigate('/gerenciamento/registro')}
                    color="primary"
                    size="medium">
                    Solicitar Novo Relatório
                </Button>
            </ContainerLateral>

            <InformacoesLimitesRelatorio />

            <TituloSecao>
                Relatórios Solicitados
            </TituloSecao>
            <RelatoriosSolicitados/>
        </Fragment>
    )
}

export default RelatoriosGerenciaisListagem;
